import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Store,
  ArrowRight
} from 'lucide-react';
import { Box } from '@mui/material';
import { useAuth } from '@/shared/contexts';
import {
  superAdminApi,
  type SuperAdminRestaurantSummary
} from '@/pages/SuperAdmin/api/superAdminApi';
import { SaDataTable, type SaColumn } from '@/pages/SuperAdmin/SaDataTable';
import { SaStatusChip } from '@/pages/SuperAdmin/SaStatusChip';
import { Alert } from '@/shared/components/ui/Alert';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';

export const SuperAdminRestaurantsPage: React.FC = () => {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<SuperAdminRestaurantSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const limit = 20;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const load = useCallback(async () => {
    if (!accessToken) return;
    setLoading(true);
    setError(null);
    try {
      const res = await superAdminApi.listRestaurants(accessToken, {
        page,
        limit,
        search: search || undefined,
        status: status || undefined
      });
      setItems(res.data.items);
      setTotal(res.data.total);
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Failed to load restaurants';
      setError(Array.isArray(message) ? message.join(', ') : String(message));
    } finally {
      setLoading(false);
    }
  }, [accessToken, page, search, status]);

  useEffect(() => {
    load();
  }, [load]);

  const columns = useMemo<SaColumn<SuperAdminRestaurantSummary>[]>(
    () => [
      {
        id: 'restaurant',
        label: 'Restaurant',
        width: '34%',
        render: row => {
          const displayName =
            row.businessName ||
            [row.firstName, row.lastName].filter(Boolean).join(' ') ||
            '—';
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '9999px',
                  bgcolor: 'rgba(0,107,130,0.1)',
                  color: '#006B82',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}
              >
                <Store className="h-4 w-4" />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Box
                  component="p"
                  sx={{
                    m: 0,
                    fontWeight: 600,
                    color: 'var(--color-text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {displayName}
                </Box>
                <Box
                  component="p"
                  sx={{
                    m: 0,
                    fontSize: 12,
                    color: 'var(--color-text-muted)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {row.phone || '—'}
                </Box>
              </Box>
            </Box>
          );
        }
      },
      {
        id: 'email',
        label: 'Email',
        width: '34%',
        render: row => (
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'var(--color-text-secondary)'
            }}
          >
            {row.email || '—'}
          </Box>
        )
      },
      {
        id: 'status',
        label: 'Status',
        width: '14%',
        render: row => <SaStatusChip status={row.status} />
      },
      {
        id: 'created',
        label: 'Created',
        width: '14%',
        render: row =>
          row.createdAt ? new Date(row.createdAt).toLocaleDateString() : '—'
      },
      {
        id: 'actions',
        label: 'Actions',
        align: 'left',
        width: 120,
        minWidth: 120,
        render: row => (
          <Link
            to={`/super-admin/restaurants/${row.id}`}
            onClick={e => e.stopPropagation()}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              fontSize: 14,
              fontWeight: 600,
              color: '#006B82',
              textDecoration: 'none',
              whiteSpace: 'nowrap'
            }}
          >
            Details
            <ArrowRight className="h-4 w-4" />
          </Link>
        )
      }
    ],
    []
  );

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-2xl font-semibold tracking-tight text-text-primary">
          Restaurants
        </h2>
        <p className="mt-1 text-sm text-text-tertiary">
          Open a restaurant for employees, suppliers, locations, and orders
        </p>
      </div>

      <div className="rounded-2xl border border-border-primary bg-background-card p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2 text-sm font-medium text-text-secondary">
          <Filter className="h-4 w-4 text-text-muted" />
          Filters
        </div>
        <div className="flex flex-wrap gap-3 items-end">
          <div className="min-w-[220px] flex-1">
            <label className="mb-1 block text-xs font-medium text-text-tertiary">
              Search
            </label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-muted" />
              <Input
                value={search}
                onChange={e => {
                  setPage(1);
                  setSearch(e.target.value);
                }}
                placeholder="Business, email, phone…"
                className="bg-background-card pl-9"
              />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-text-tertiary">
              Status
            </label>
            <select
              className="block h-10 rounded-md border border-border-primary bg-background-card px-3 text-sm"
              value={status}
              onChange={e => {
                setPage(1);
                setStatus(e.target.value);
              }}
            >
              <option value="">All statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>
      </div>

      {error && <Alert variant="destructive">{error}</Alert>}

      {loading ? (
        <div className="flex justify-center rounded-2xl border border-border-primary bg-background-card py-16 shadow-sm">
          <Loader2 className="h-7 w-7 animate-spin text-[#006B82]" />
        </div>
      ) : (
        <SaDataTable
          columns={columns}
          rows={items}
          getRowId={row => row.id}
          emptyMessage="No invite or signup restaurants found"
          onRowClick={row => navigate(`/super-admin/restaurants/${row.id}`)}
        />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-text-secondary">
        <span>
          {total} restaurant{total === 1 ? '' : 's'} · Page {page} of{' '}
          {totalPages}
        </span>
        <div className="flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1"
            disabled={page <= 1}
            onClick={() => setPage(p => Math.max(1, p - 1))}
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1"
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};
